import React from 'react';

import classNames from 'classnames';

import { Header, Layout } from 'components/Layout';
import { NavigationBar } from 'components/NavigationBar';

import styles from './TermsOfServicePage.module.scss';

const TermsOfServicePage = () => {
  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.header}>
          <Header hasBoxShadow>
            <NavigationBar hasFullLogo />
          </Header>
        </div>

        <div className={styles.content}>
          <h1 className={styles.title}>FirstIn Patient Terms and Conditions</h1>

          <p className={classNames(styles.paragraph, styles.meta)}>
            <strong>Effective date:</strong> August 13, 2026
          </p>

          <div className={styles.notice}>
            <strong>Emergency warning:</strong> FirstIn is not an emergency
            service. Do not use the scheduling widget, an "Emergency" appointment
            option, or a free-text note for a medical or dental emergency. Call
            emergency services or seek immediate professional care as
            appropriate.
          </div>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              1. Acceptance of these Terms
            </h2>

            <p className={styles.paragraph}>
              These Patient Terms and Conditions ("Terms") govern your use of
              websites, online scheduling widgets, booking links, verification
              services, patient accounts, and related communication or payment
              features provided by FirstIn ("FirstIn," "we," "us," or "our") for
              participating dental practices (collectively, the "Patient
              Services"). FirstIn is operated by FirstIn Practice Solutions LTD.
            </p>

            <p className={styles.paragraph}>
              By accessing or using the Patient Services, selecting an
              acknowledgment checkbox, or submitting a booking request, you agree
              to these Terms. If you schedule for another person, you also confirm
              that you are authorized to act for that person and to provide their
              information.
            </p>

            <p className={styles.paragraph}>
              The selected dental practice may impose separate clinical, privacy,
              financial, deposit, cancellation, and no-show policies. Those
              practice policies form an agreement between you and the practice,
              not FirstIn, unless expressly stated otherwise.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              2. FirstIn provides technology, not dental care
            </h2>

            <p className={styles.paragraph}>
              FirstIn supplies scheduling and related technology. FirstIn is not
              a dentist, healthcare provider, insurer, benefits administrator,
              pharmacy, emergency service, or agent responsible for a practice's
              clinical decisions.
            </p>

            <p className={styles.paragraph}>
              The dental practice is solely responsible for:
            </p>

            <ul className={styles.list}>
              <li>
                Dental advice, diagnoses, treatment, clinical outcomes,
                prescriptions, referrals, and professional conduct.
              </li>
              <li>
                Provider qualifications, services, office hours, locations,
                appointment availability, and accessibility.
              </li>
              <li>
                Prices, estimates, insurance participation, benefit verification,
                billing, refunds, deposits, cancellation charges, and no-show
                fees.
              </li>
              <li>
                Its patient records, privacy notices, legal duties, and
                communications with patients.
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              3. Eligibility and authority
            </h2>

            <p className={styles.paragraph}>
              You may use the Patient Services only if you can legally agree to
              these Terms. If you schedule for a child, dependant, family member,
              or other person, you represent that you are the patient, the
              patient's parent or legal guardian, or otherwise authorized to
              schedule and provide information for that person.
            </p>

            <p className={styles.paragraph}>
              You must not search for, view, or use another person's patient
              information without authorization. A shared phone, family
              relationship, possession of a verification code, or knowledge of a
              date of birth does not by itself establish legal authority.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              4. Patient matching, sign-in, and verification
            </h2>

            <p className={styles.paragraph}>
              A practice may configure the Patient Services to match information
              such as a date of birth and phone number against its records.
              FirstIn may send a one-time verification code by text message before
              displaying matched information. You authorize FirstIn and its
              communications provider to send that requested verification message
              to the number you enter. Message and data rates may apply.
            </p>

            <p className={styles.paragraph}>
              You are responsible for entering a phone number you control or are
              authorized to use, protecting verification codes, and preventing
              unauthorized access to your device or account. Do not proceed if
              the Patient Services display a person whom you are not authorized
              to represent. Use the "not you" option or contact the practice.
            </p>

            <p className={styles.paragraph}>
              If you use Google, Facebook, or another third-party sign-in method,
              your use of that provider is also subject to its terms and privacy
              policy. FirstIn does not control the provider's service.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>5. Accurate information</h2>

            <p className={styles.paragraph}>
              You agree to provide current, accurate, and complete information and
              to review all displayed patient and appointment details before
              submission. Do not impersonate another person, provide false
              insurance information, enter unlawful content, or use the Patient
              Services to obtain unauthorized access.
            </p>

            <p className={styles.paragraph}>
              Free-text notes may be transmitted to the dental practice and become
              part of its records. Do not submit unnecessary sensitive
              information, payment-card numbers, passwords, or emergency requests
              in a note.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              6. Appointment requests and confirmations
            </h2>

            <p className={styles.paragraph}>
              Availability displayed by the Patient Services may change. Selecting
              a time or pressing "Submit," "Book Appointment," or a similar button
              sends a booking request or attempts to create an appointment in the
              practice's connected system. An appointment is not guaranteed until
              the practice or its connected system confirms it.
            </p>

            <p className={styles.paragraph}>
              The practice may review, modify, reschedule, cancel, or decline a
              request. Technical delays, synchronization errors, duplicate
              records, network failures, stale availability, or practice-system
              outages may affect a booking. If you do not receive confirmation or
              if the details appear incorrect, contact the practice directly.
            </p>

            <p className={styles.paragraph}>
              FirstIn does not guarantee that a requested provider will personally
              perform all services, that an appointment will start on time, or
              that a displayed service is clinically appropriate.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              7. Emergency and urgent needs
            </h2>

            <p className={styles.paragraph}>
              The Patient Services are not monitored continuously and are not
              appropriate for emergencies. Selecting an appointment reason
              labelled "Emergency" only identifies a scheduling category offered
              by the practice. It does not notify emergency responders, create a
              promise of immediate treatment, or establish clinical triage.
            </p>

            <p className={styles.paragraph}>
              If you believe you are experiencing a medical or dental emergency,
              call 911 or the applicable emergency number, go to an emergency
              department, or contact an appropriate licensed professional
              immediately.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              8. Insurance and self-pay information
            </h2>

            <p className={styles.paragraph}>
              An insurer appearing in a list, or a statement that an insurer is
              accepted or "in-network," is informational only and may be
              incomplete, outdated, location-specific, provider-specific,
              plan-specific, or subject to change. It is not verification of
              eligibility, benefits, coverage, authorization, reimbursement, or
              price.
            </p>

            <p className={styles.paragraph}>
              You are responsible for confirming coverage and financial
              responsibility with the dental practice and insurer. You remain
              responsible for charges not paid by insurance unless applicable law
              or a written practice agreement provides otherwise.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              9. Deposits, payments, cancellation, and no-show fees
            </h2>

            <p className={styles.paragraph}>
              A practice may require a deposit, payment, card authorization, or
              acceptance of a cancellation or no-show policy. Any applicable
              amount, timing, refund rule, cancellation deadline, and charging
              condition should be disclosed before you authorize the transaction.
            </p>

            <p className={styles.paragraph}>
              Charges are imposed by or for the dental practice under its
              financial policies. FirstIn may provide the technology or engage a
              payment processor but does not independently set the practice's
              fees. Payment processing may be subject to the processor's terms
              and privacy policy.
            </p>

            <p className={styles.paragraph}>
              No cancellation, no-show, deposit, or card charge is authorized
              merely because it is discussed generally in these Terms. A practice
              must provide any disclosure and obtain any separate authorization
              required by law.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              10. Appointment and marketing communications
            </h2>

            <p className={styles.paragraph}>
              By requesting a verification code or submitting a booking, you
              consent to transactional texts, calls, or emails reasonably
              necessary to verify your identity, administer the request, confirm
              or change appointments, provide wait-list updates, address payments,
              or deliver service notices. Message and data rates may apply.
              Delivery may fail or be delayed.
            </p>

            <p className={styles.paragraph}>
              Consent to marketing must be obtained separately when required and
              is not a condition of booking. You may withdraw marketing consent
              using the method stated in the message. Opting out of marketing does
              not necessarily stop verification, security, or appointment-related
              messages. Contact the practice if you need to change essential
              communication preferences.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              11. Privacy and embedded practice websites
            </h2>

            <p className={styles.paragraph}>
              FirstIn's Patient Privacy Policy describes FirstIn's handling of
              information. The dental practice's privacy notice describes the
              practice's handling of information. If the widget is embedded in a
              practice website, that practice may independently use Google
              Analytics, Meta Pixel, advertising tags, cookies, session replay, or
              similar tools on the surrounding website.
            </p>

            <p className={styles.paragraph}>
              Practices and their vendors are not permitted to capture sensitive
              widget information for advertising or profiling. FirstIn does not
              authorize the use of patient identifiers, appointment details,
              insurance information, symptoms, or health-related notes for
              targeted advertising. The surrounding practice website remains
              subject to the practice's own configuration, notices, consent
              obligations, and legal responsibilities.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>12. Acceptable use</h2>

            <p className={styles.paragraph}>You must not:</p>

            <ul className={styles.list}>
              <li>
                Access patient information without authorization or use automated
                means to enumerate or test patient records.
              </li>
              <li>Interfere with security, verification, availability, rate limits, or connected systems.</li>
              <li>
                Introduce malware, scrape the Patient Services, reverse engineer
                protected portions, or use the service for unlawful purposes.
              </li>
              <li>
                Submit content that is fraudulent, harmful, threatening,
                infringing, discriminatory, or unlawful.
              </li>
              <li>Use the Patient Services to advertise, solicit, or send unsolicited messages.</li>
            </ul>

            <p className={styles.paragraph}>
              We may restrict or suspend use when reasonably necessary to protect
              patients, practices, FirstIn, or the Patient Services.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              13. Intellectual property
            </h2>

            <p className={styles.paragraph}>
              FirstIn and its licensors own the Patient Services, software,
              design, trademarks, and related intellectual property. Subject to
              these Terms, FirstIn grants you a limited, personal, revocable,
              non-exclusive, non-transferable right to use the Patient Services
              for legitimate patient scheduling and related purposes.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              14. Third-party services and links
            </h2>

            <p className={styles.paragraph}>
              The Patient Services may interoperate with practice-management
              systems, payment processors, communications providers, sign-in
              providers, maps, practice websites, and other third-party services.
              FirstIn does not control third-party services and is not responsible
              for their separate terms, privacy practices, availability, or
              content.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>15. Disclaimers</h2>

            <p className={styles.paragraph}>
              To the maximum extent permitted by law, the Patient Services are
              provided "as is" and "as available." FirstIn disclaims implied
              warranties of merchantability, fitness for a particular purpose,
              non-infringement, and warranties arising from course of dealing or
              usage.
            </p>

            <p className={styles.paragraph}>
              FirstIn does not warrant that the Patient Services will be
              uninterrupted, error-free, secure, or current; that availability,
              provider, insurer, price, or patient-matching information will be
              accurate; or that a message or booking will be received, confirmed,
              or acted upon.
            </p>

            <p className={styles.paragraph}>
              Nothing in these Terms excludes a warranty, condition, right, or
              remedy that cannot lawfully be excluded.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              16. Limitation of liability
            </h2>

            <p className={styles.paragraph}>
              To the maximum extent permitted by law, FirstIn will not be liable
              for indirect, incidental, special, consequential, exemplary, or
              punitive damages, or for lost profits, revenues, goodwill,
              opportunities, or data arising from the Patient Services.
            </p>

            <p className={styles.paragraph}>
              FirstIn is not responsible for dental care, clinical outcomes,
              practice conduct, insurance decisions, practice charges, missed
              appointments caused by inaccurate information or failed
              communications, or independent tracking operated by a practice on
              its surrounding website.
            </p>

            <p className={styles.paragraph}>
              Where liability cannot be excluded, FirstIn's aggregate liability
              arising from a patient's use of the Patient Services will be limited
              to the greater of the amount the patient paid directly to FirstIn
              for the Patient Services during the preceding twelve months or CAD
              $100, except where a different limit is required by law.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>17. Indemnity</h2>

            <p className={styles.paragraph}>
              To the extent permitted by law, you agree to indemnify FirstIn from
              third-party claims resulting from your intentional misuse of the
              Patient Services, unauthorized access to another person's
              information, fraudulent submissions, or violation of another
              person's rights. This provision does not apply to the extent a claim
              results from FirstIn's own unlawful conduct.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              18. Suspension and termination
            </h2>

            <p className={styles.paragraph}>
              You may stop using the Patient Services at any time. FirstIn may
              suspend or terminate access to protect security, comply with law,
              respond to a practice's instruction, prevent abuse, or discontinue a
              feature. Termination does not cancel an appointment already created;
              contact the practice directly to change or cancel it.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              19. Governing law and disputes
            </h2>

            <p className={styles.paragraph}>
              Except where mandatory consumer or privacy law provides otherwise,
              these Terms are governed by the laws of the Province of Alberta and
              the federal laws of Canada applicable there, without regard to
              conflict-of-law rules. The parties submit to the courts of Alberta
              for disputes that cannot be resolved informally.
            </p>

            <p className={styles.paragraph}>
              Before filing a claim, contact FirstIn and provide a reasonable
              opportunity to resolve it. Nothing in these Terms prevents a person
              from contacting a privacy, consumer-protection, health, or other
              regulator or from pursuing rights that cannot lawfully be waived.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              20. Changes to the Patient Services or these Terms
            </h2>

            <p className={styles.paragraph}>
              We may change the Patient Services or these Terms. We will post an
              updated version with a new effective date and provide additional
              notice when required. Material changes do not retroactively
              authorize a charge or waive an existing legal right.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>21. General provisions</h2>

            <p className={styles.paragraph}>
              These Terms, the Patient Privacy Policy, and any acknowledgment
              presented during the booking flow form the agreement between you and
              FirstIn concerning the Patient Services. Practice policies remain
              separate agreements with the practice. If a provision is
              unenforceable, it will be limited to the minimum extent necessary
              and the remaining provisions will continue. A failure to enforce a
              provision is not a waiver. You may not assign these Terms without
              FirstIn's consent; FirstIn may assign them as part of a corporate
              transaction or by operation of law.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>22. Contact</h2>

            <p className={styles.paragraph}>
              For appointments, care, insurance, charges, refunds, cancellations,
              or patient records, contact the selected dental practice.
            </p>

            <p className={styles.paragraph}>
              For questions about the Patient Services or these Terms, contact:
            </p>

            <p className={styles.paragraph}>
              <strong>
                FirstIn Practice Solutions LTD.
                <br />
                Edmonton, Alberta, Canada
                <br />
                <a className={styles.link} href="mailto:support@firstin.io">
                  support@firstin.io
                </a>
              </strong>
            </p>

            <p className={classNames(styles.paragraph, styles['footer-line'])}>
              FirstIn Practice Solutions LTD. | Edmonton, Alberta, Canada |{' '}
              <a className={styles.link} href="mailto:support@firstin.io">
                support@firstin.io
              </a>
            </p>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default TermsOfServicePage;
